import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Heart, RefreshCw, CheckCircle2, MessageCircle, XCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "pending",   label: "En attente",  color: "bg-amber-500 text-white" },
  { key: "contacted", label: "Contactés",   color: "bg-blue-500 text-white" },
  { key: "onboarded", label: "Intégrés",    color: "bg-emerald-500 text-white" },
  { key: "rejected",  label: "Refusés",     color: "bg-rose-500 text-white" },
  { key: "all",       label: "Tout",         color: "bg-slate-200 text-slate-700" },
];

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-800",
  contacted: "bg-blue-100 text-blue-800",
  onboarded: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
};

export default function AdminPartnerRecommendations() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [commentById, setCommentById] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const url = `/admin/partner-recommendations${tab && tab !== "all" ? `?status=${tab}` : ""}`;
      const { data } = await api.get(url);
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch { toast.error("Erreur de chargement."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const update = async (rec, status) => {
    setBusy(rec.id);
    try {
      await api.patch(`/admin/partner-recommendations/${rec.id}`, {
        status, admin_comment: commentById[rec.id] || "",
      });
      toast.success("Mis à jour.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setBusy(""); }
  };

  const remove = async (rec) => {
    if (!window.confirm(`Supprimer la recommandation « ${rec.business_name} » ?`)) return;
    setBusy(rec.id);
    try {
      await api.delete(`/admin/partner-recommendations/${rec.id}`);
      toast.success("Supprimé.");
      await load();
    } catch { toast.error("Erreur."); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="admin-partner-recos-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2" data-testid="admin-recos-title">
            <Heart className="text-rose-600" size={26}/> Recommandations de commerces
          </h1>
          <p className="text-slate-500 text-xs mt-1">Commerces recommandés par les habitants pour rejoindre le réseau.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-recos-reload">
          <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`}/> Actualiser
        </Button>
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { k: "total", l: "Total", c: "text-slate-900" },
          { k: "pending", l: "En attente", c: "text-amber-600" },
          { k: "contacted", l: "Contactés", c: "text-blue-600" },
          { k: "onboarded", l: "Intégrés", c: "text-emerald-600" },
          { k: "rejected", l: "Refusés", c: "text-rose-600" },
        ].map((s, i) => (
          <div key={s.k} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`admin-recos-stat-${s.k}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{s.l}</div>
            <div className={`font-heading text-2xl font-extrabold mt-1 ${s.c}`}>{counts[s.k] || 0}</div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border ${tab === t.key ? t.color + " border-transparent shadow" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}
                  data-testid={`admin-recos-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      {loading ? <div className="mt-6 text-slate-500 text-sm">Chargement...</div> :
       items.length === 0 ? <div className="mt-6 bg-white border border-dashed rounded-2xl p-10 text-center text-slate-500 text-sm">Aucune recommandation dans cette catégorie.</div> :
       <div className="mt-4 space-y-3">
         {items.map((r) => (
           <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`admin-reco-row-${r.id}`}>
             <div className="flex flex-wrap items-start justify-between gap-3">
               <div className="flex-1 min-w-0">
                 <div className="flex items-center gap-2 flex-wrap">
                   <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_BADGE[r.status] || "bg-slate-100"}`}>{r.status}</span>
                   {r.category && <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-bold uppercase">{r.category}</span>}
                 </div>
                 <div className="mt-1 font-heading font-bold text-slate-900">{r.business_name}</div>
                 <div className="text-xs text-slate-600 mt-0.5">
                   📍 {r.city} {r.contact_info && <>· {r.contact_info}</>}
                 </div>
                 <div className="text-xs text-slate-500 mt-1">
                   Recommandé par : <b>{r.sender_name || "anonyme"}</b>
                   {r.sender_email && <> · <a href={`mailto:${r.sender_email}`} className="text-blue-600">{r.sender_email}</a></>}
                 </div>
                 {r.message && <div className="mt-2 text-sm text-slate-700 whitespace-pre-line bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">{r.message}</div>}
                 {r.admin_comment && <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 text-amber-900"><b>Note admin :</b> {r.admin_comment}</div>}
                 <div className="mt-1 text-[11px] text-slate-400">{new Date(r.created_at).toLocaleString("fr-FR")}</div>
               </div>
             </div>
             <div className="mt-3">
               <Textarea rows={2} placeholder="Note interne (facultatif)" value={commentById[r.id] || ""}
                         onChange={(e) => setCommentById((s) => ({...s, [r.id]: e.target.value}))}
                         data-testid={`admin-reco-comment-${r.id}`}/>
             </div>
             <div className="mt-2 flex flex-wrap justify-end gap-2">
               {r.status !== "contacted" && (
                 <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => update(r, "contacted")} data-testid={`admin-reco-contacted-${r.id}`}>
                   <MessageCircle size={13} className="mr-1 text-blue-600"/> Marquer contacté
                 </Button>
               )}
               {r.status !== "onboarded" && (
                 <Button size="sm" disabled={busy === r.id} onClick={() => update(r, "onboarded")} className="btn-shine text-white" data-testid={`admin-reco-onboarded-${r.id}`}>
                   <CheckCircle2 size={13} className="mr-1"/> Intégré au réseau
                 </Button>
               )}
               {r.status !== "rejected" && (
                 <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => update(r, "rejected")} className="text-rose-600" data-testid={`admin-reco-rejected-${r.id}`}>
                   <XCircle size={13} className="mr-1"/> Refuser
                 </Button>
               )}
               <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => remove(r)} className="text-slate-500" data-testid={`admin-reco-delete-${r.id}`}>
                 <Trash2 size={13}/>
               </Button>
             </div>
           </div>
         ))}
       </div>}
    </div>
  );
}
